/**
 * MatchDetailScreen — page shell for `/match/:eventId`.
 *
 * Calls `useMatchDetail()` (P3) to fetch the IHS bundle and renders:
 *   1. A minimal match header (home / away + kickoff)
 *   2. The IHS bundle via `<IhsSlot widgets=... />`
 *   3. A footer placeholder (P9 ComplianceFooter renders elsewhere in
 *      the shell)
 *
 * Loading / error / empty states use i18n keys with English defaults
 * (`match.loading`, `match.error`, `match.empty`). The screen itself
 * does NOT implement routing — P8 hooks it into the React Router tree.
 *
 * Tenant slug: the engine client carries the tenant slug internally
 * (resolved in P3), so the hook signature stays single-arg
 * (`useMatchDetail(eventId)`). The slug-aware fetch is one layer down.
 */

import { useSearchParams } from 'react-router-dom';
import { useEffect, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from '../../i18n/index.js';
import { useMatchDetail } from '../../hooks/useMatchDetail.js';
import { useInView } from '../../hooks/useInView.js';
import { useTenantConfig } from '../../tenant/useTenantConfig.js';
import {
  beginUnitTelemetryPageView,
  recordUnitEvent,
} from '../../lib/unitTelemetry.js';
import { MarketingVariantsLink } from '../atoms/MarketingVariantsLink.js';
import { ComponentGate } from '../system/ComponentGate.js';
import { ErrorBoundary } from '../particles/ErrorBoundary.js';
import { DefaultCard, type DefaultCardArticle } from '../widgets/DefaultCard.js';
import { MatchHeader } from '../widgets/MatchHeader.js';
// #456 — ProseStoriesRail removed. Per-market audio now lives INSIDE
// each MarketSection in MultiMarketStack (the audio for the 1X2
// analysis lives inside the 1X2 collapsible card, etc.).
import {
  DeckAllMarketsDisclosure,
  DeckMarketSection,
  MarketLayoutsProvider,
  MultiMarketStack,
  OrphanGems,
} from '../widgets/MultiMarketStack.js';
import { PlayerPropsCard } from '../widgets/PlayerPropsCard.js';
// MatchInsights (aggregate card) retired 2026-08-01 — gem atoms attach
// to market sections; orphans render via OrphanGems.
import { LayoutRenderer } from '../layout/LayoutRenderer.js';
import { IhsSlot } from './IhsSlot.js';
import { SectionHeader } from '../primitives/SectionHeader.js';
import { HeroLeadCard } from '../widgets/HeroLeadCard.js';
import { DidYouKnowRail } from '../widgets/DidYouKnowRail.js';
import { useLeadStory } from '../../hooks/useLeadStory.js';
import styles from './MatchDetailScreen.module.css';

interface MatchDetailScreenProps {
  /** Event id from the URL, e.g. `sr:sport_event:72221292`. */
  eventId: string;
}

export function MatchDetailScreen({
  eventId,
}: MatchDetailScreenProps): ReactElement {
  const { t } = useTranslation();
  // Phase 5G — read ?market=X from URL. When set + tenant has feature
  // flag, engine ships marketLayout (AI-composed primitive layout). The
  // screen prepends it above the IHS bundle; bundle stays intact for
  // graceful degradation. Absent => bundle-only (existing behaviour).
  const [searchParams] = useSearchParams();
  const marketFocus = searchParams.get('market') ?? undefined;
  const { data, loading, error, refresh } = useMatchDetail(eventId, marketFocus);
  // Dramatization Engine P3 — the dramatized hero lead story (self-hides null).
  const { leadStory } = useLeadStory(eventId);
  // Telemetry-lite (Phase 3) — beacon endpoint for the ranked-deck path,
  // built from the same engine baseUrl + slug every other feed call uses.
  // Null while tenant config hasn't resolved (no beacons, page unaffected).
  const tenant = useTenantConfig();
  const telemetryUrl =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? `${tenant.config.engine.baseUrl.replace(/\/+$/, '')}/feed/${encodeURIComponent(
          tenant.config.tenant.slug,
        )}/unit-events`
      : null;

  // Engine-degraded mode (Lag 2 of self-heal triple play): the engine
  // couldn't reach Sportradar AND had no LKG to fall back on, so it
  // shipped a stub with degradedMode: true. The hook retries with backoff
  // and then polls every 30s in the background.
  //
  // Audit 2026-09-03: this used to hold "Loading match…" forever, which
  // during a >24h provider outage meant an infinite spinner — a silent
  // fallback on the punter's screen. While the hook is still loading we
  // show the loading state; once it has given up (loading=false, stub in
  // hand) we say so honestly and offer a retry. Background polling keeps
  // running, so the page still heals itself when upstream recovers.
  if (loading) {
    return (
      <div className={styles.screen}>
        <div className={styles.state} role="status" aria-busy="true" aria-live="polite">
          {t('match.loading', { defaultValue: 'Loading match…' })}
        </div>
      </div>
    );
  }

  if (data?.degradedMode === true) {
    return (
      <div className={styles.screen}>
        <div className={styles.state} role="status" aria-live="polite">
          <p>{t('match.degraded', { defaultValue: 'Match data is temporarily unavailable. Please try again shortly.' })}</p>
          <button type="button" className={styles.retryButton} onClick={(): void => { void refresh(); }}>
            {t('match.retry', { defaultValue: 'Try again' })}
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.screen}>
        <div className={styles.state} role="alert">
          {t('match.error', { defaultValue: 'Could not load this match.' })}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.screen}>
        <div className={styles.state}>
          {t('match.empty', { defaultValue: 'No match data available.' })}
        </div>
      </div>
    );
  }

  const { match, widgets, article } = data;
  // Phase 5G — engine ships marketLayout only when ?market=X + tenant
  // flag + plan was non-trivial. Defensive cast: shared-types types it
  // as `unknown`; LayoutRenderer's Zod schema validates the actual shape
  // and silently drops the section on a malformed payload.
  const marketLayout = (data as { marketLayout?: unknown }).marketLayout;

  const hasDeck = Boolean(data.deck && data.deck.length > 0);
  if (hasDeck && telemetryUrl) {
    // Deliberately during render, not in an effect: child effects (the
    // DeckUnitObserver impressions below) run BEFORE this parent's own
    // effects, so an effect here would drop the first impressions.
    // beginUnitTelemetryPageView is idempotent per (url, eventId), which
    // makes the render-time call safe under StrictMode double-invoke.
    beginUnitTelemetryPageView({ url: telemetryUrl, eventId });
  }

  // The fixture's own ground, behind the header. The engine already ships
  // real stadium photography on the match-detail article; the header is
  // where it belongs (the picture is the page's one piece of graphic
  // weight), scrimmed so the book below stays the loudest thing here.
  const headerImage =
    (article?.['heroImage'] as { url?: string } | undefined)?.url ?? null;

  // The stat block opens with its own header the first time a stat widget
  // appears in the deck. The engine owns the ORDER (deck array order); the
  // render only names the block when it starts.
  let statsHeaderDrawn = false;

  return (
    <div className={styles.screen}>
      <MatchHeader match={match} image={headerImage} />

      <div className={styles.content}>
        {/* Dramatization Engine P3 — the dramatized "back-page lead": the
            single most spectacular mined gem, with its themed odds as the
            payoff. Leads the page (above the article + widgets). Self-hides
            when the engine returns no lead story. */}
        {leadStory ? (
          <ErrorBoundary>
            <HeroLeadCard story={leadStory} />
          </ErrorBoundary>
        ) : null}

        {/* Dramatization Engine P4 — "Did you know?" support rail. The
            secondary dramatized angles for this fixture, each with its own
            archetype chip + themed odds. Self-hides when the engine ships
            no support items (and on older cached lead-story payloads). */}
        {leadStory?.support && leadStory.support.length > 0 ? (
          <ErrorBoundary>
            <DidYouKnowRail support={leadStory.support} />
          </ErrorBoundary>
        ) : null}

        {/* Featured article — when the engine ships one (eventId match
            on a live ContentPackage), it leads the page so the visitor
            sees AI-written analysis before raw stat widgets. Click
            navigates to the full article. */}
        {article && typeof article['headline'] === 'string' ? (
          <ErrorBoundary>
            {/* The same card as the hub's round stack (DefaultCard) — one
                component per concept. The fixture's names come from the
                match, the rest from the article the engine ships. */}
            <DefaultCard
              article={{
                id: String(article['id'] ?? ''),
                headline: String(article['headline']),
                manchet: typeof article['manchet'] === 'string' ? article['manchet'] : '',
                readTimeMinutes:
                  typeof article['readTimeMinutes'] === 'number'
                    ? article['readTimeMinutes']
                    : undefined,
                journalistName:
                  typeof article['journalistName'] === 'string'
                    ? article['journalistName']
                    : undefined,
                heroImage: (article['heroImage'] as DefaultCardArticle['heroImage']) ?? null,
                match: { homeTeam: match.homeTeam.name, awayTeam: match.awayTeam.name },
              }}
            />
          </ErrorBoundary>
        ) : null}

        {/* #456 — standalone prose-Stories rail removed. The per-market
            AI audio now lives INSIDE each MarketSection (collapsible
            card) in MultiMarketStack below — the 1X2 analysis sits in
            the 1X2 card, BTTS in BTTS, etc. Users hear the take for the
            market they're looking at, not a chip rail abstracted from
            the stats it argues. */}

        {/* Phase 5G — market-tuned LayoutPlan. Present only when the
            URL has ?market=X AND the tenant has features_layout_planner_enabled.
            Sits ABOVE the IHS bundle so the AI's market-specific
            primitive composition primes the page; the rich IHS widgets
            below remain the source of full data. ErrorBoundary +
            LayoutRenderer's internal Zod validation mean a malformed
            plan can't break the page — bundle still renders. */}
        {marketLayout ? (
          <ErrorBoundary>
            <LayoutRenderer plan={marketLayout as Parameters<typeof LayoutRenderer>[0]['plan']} />
          </ErrorBoundary>
        ) : null}

        {/* Ranked deck — when the engine ships a non-empty `deck`, it owns
            the ORDER of the whole page middle: render deals every unit
            exactly once, in deck order, and does NOT also render the
            legacy blocks below. Same principle as the IHS bundle (engine
            controls layout via array order; render never re-orders),
            extended from widgets[] to the middle. Each unit gets the same
            ErrorBoundary isolation as the legacy blocks. The
            MarketLayoutsProvider wraps the whole middle so every
            `market_card` unit shares ONE market-layouts fetch (+ warm +
            audio state) — exactly what MultiMarketStack does internally
            on the legacy path. Absent/empty deck → the legacy fixed order
            below, unchanged. */}
        {data.deck && data.deck.length > 0 ? (
          <MarketLayoutsProvider eventId={eventId}>
            {data.deck.map((unit, index) => {
              const key = `${unit.kind}:${unit.ref}:${index}`;
              // Telemetry-lite (Phase 3): every dealt unit is wrapped in
              // a DeckUnitObserver (impression on visibility); the two
              // odds-bearing units additionally thread expand/odds_click
              // through their existing seams. All recording is deduped
              // per page view and fail-soft in the telemetry queue.
              switch (unit.kind) {
                case 'insights':
                  // Gem-atom program (2026-08-01): the aggregate "Most
                  // interesting" card is RETIRED. Gems now attach to
                  // their own market sections (renderSectionEntry);
                  // this unit renders only the orphans — jewels whose
                  // market has no section on the page.
                  return (
                    <ErrorBoundary key={key}>
                      <DeckUnitObserver kind={unit.kind} unitRef={unit.ref}>
                        <OrphanGems />
                      </DeckUnitObserver>
                    </ErrorBoundary>
                  );
                case 'market_card':
                  return (
                    <ErrorBoundary key={key}>
                      <DeckUnitObserver kind={unit.kind} unitRef={unit.ref}>
                        <DeckMarketSection
                          marketId={unit.ref}
                          onExpand={(): void =>
                            recordUnitEvent(unit.kind, unit.ref, 'expand')
                          }
                          onOddsClick={(): void =>
                            recordUnitEvent(unit.kind, unit.ref, 'odds_click')
                          }
                        />
                      </DeckUnitObserver>
                    </ErrorBoundary>
                  );
                case 'player_props':
                  return (
                    <ErrorBoundary key={key}>
                      <DeckUnitObserver kind={unit.kind} unitRef={unit.ref}>
                        <PlayerPropsCard eventId={eventId} />
                      </DeckUnitObserver>
                    </ErrorBoundary>
                  );
                case 'stat_widget': {
                  // Widgets come from the same match-detail payload; an
                  // unknown/missing ref renders nothing — silently
                  // consistent with IhsSlot's unknown-template handling.
                  const widget = widgets.find((w) => w.template === unit.ref);
                  if (!widget) return null;
                  const openStats = !statsHeaderDrawn;
                  statsHeaderDrawn = true;
                  return (
                    <ErrorBoundary key={key}>
                      {openStats ? (
                        <SectionHeader
                          inset
                          title={t('match.statsSection', {
                            defaultValue: 'Match statistics',
                          })}
                        />
                      ) : null}
                      <DeckUnitObserver kind={unit.kind} unitRef={unit.ref}>
                        <IhsSlot widgets={[widget]} />
                      </DeckUnitObserver>
                    </ErrorBoundary>
                  );
                }
                default:
                  // Unknown unit kind from a newer engine — skip silently.
                  return null;
              }
            })}
            {/* C2 — the odds diet: every non-featured market the deck
                skipped inline, behind one "all markets" disclosure. */}
            <DeckAllMarketsDisclosure />
          </MarketLayoutsProvider>
        ) : (
          <>
            {/* Gem-atom program (2026-08-01): the aggregate jewels card is
                retired — gems attach directly to their market sections
                inside MultiMarketStack (shared renderSectionEntry). */}
            {/* #409 Phase 3 — multi-market scroll. Reads cached LayoutPlans
                via GET /feed/.../market-layouts (no Haiku). Cached markets
                render full LayoutRenderer body; pending markets show
                skeleton shimmer with the marketId label visible. Phase 4
                will add auto-warm POST + polling so pending sections fill
                in over time. Self-hides cleanly on fetch error so a hiccup
                here never blocks the IHS bundle below. */}
            <ErrorBoundary>
              <MultiMarketStack eventId={eventId} />
            </ErrorBoundary>

            {/* Anytime-goalscorer list — a ranked player list (a different shape
                from the market cards above). Self-hides when no player props are
                priced. The full market COVERAGE now lives in MultiMarketStack
                itself (uncapped), so there's no separate odds board. */}
            <ErrorBoundary>
              <PlayerPropsCard eventId={eventId} />
            </ErrorBoundary>

            {widgets.length > 0 ? (
              <SectionHeader
                inset
                title={t('match.statsSection', { defaultValue: 'Match statistics' })}
              />
            ) : null}
            <ErrorBoundary>
              <IhsSlot widgets={widgets} />
            </ErrorBoundary>
          </>
        )}

        {/* #213 deep-link to admin /variants for this match. Discreet
            footer pill — operator-facing; punters get the auth wall. */}
        <ComponentGate id="marketing-variants-link"><MarketingVariantsLink eventId={eventId} /></ComponentGate>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DeckUnitObserver — telemetry-lite impression observer (Phase 3).
//
// A bare wrapper div (no class, no styling) whose only job is to be an
// IntersectionObserver target via the house useInView hook. `once: false`
// on purpose: useInView's once-mode carries the 1.5s reveal FAILSAFE that
// force-fires `inView` for animation purposes — that would count every
// off-screen unit as "seen". With once:false the boolean tracks real
// viewport state and the telemetry queue's per-page-view dedupe makes only
// the FIRST sighting count. (Known prototype trade-off: reduced-motion
// users report inView immediately by the hook's design, slightly
// overcounting their impressions.)
// ---------------------------------------------------------------------------

interface DeckUnitObserverProps {
  kind: string;
  unitRef: string;
  children: ReactNode;
}

function DeckUnitObserver({
  kind,
  unitRef,
  children,
}: DeckUnitObserverProps): ReactElement {
  const { ref, inView } = useInView<HTMLDivElement>({ once: false });
  useEffect(() => {
    if (inView) recordUnitEvent(kind, unitRef, 'impression');
  }, [inView, kind, unitRef]);
  return <div ref={ref}>{children}</div>;
}

export default MatchDetailScreen;
